import { Injectable } from '@angular/core';
import { Category, ICategoryData, ICategoryDataV3 } from '@df/catalog/category/category';
import { CategoryResource } from '@df/catalog/category/category.resource';
import { App } from '@df/core/app';
import { ICmsBlock, NICmsBlock } from '@icms/core/resource/block/icms-block';
import { ICmsBlockResource } from '@icms/core/resource/block/icms-block.resource';
import difference from 'lodash-es/difference';
import intersection from 'lodash-es/intersection';
import isEqual from 'lodash-es/isEqual';
import { NICmsMerchandiser } from '../../config/icms-merchandiser.cms-config';

declare module '@df/core/app' {
    interface App {
        iCmsMerchandiserService: ICmsMerchandiserService;
    }
}

declare module '@df/catalog/category/category' {
    interface ICategoryDataV3 {
        _vmProducts?: number[];
    }
}

@Injectable({ providedIn: 'root' })
export class ICmsMerchandiserService {
    private loadedBlocks = new Map<number, ICmsBlock<NICmsBlock.IData<NICmsMerchandiser.IData>>>();

    constructor(
        app: App,
        private blockResource: ICmsBlockResource,
        private categoryResource: CategoryResource
    ) {
        app.iCmsMerchandiserService = this;
        this.pluginCategoryResource();
    }

    /**
     * Retrieve config for category
     * if not yet loaded then either load or create and set up all the listeners
     */
    async provideConfig(categoryId: number): Promise<ICmsBlock<NICmsBlock.IData<NICmsMerchandiser.IData>> | void> {
        // load config
        if (!this.loadedBlocks.has(categoryId)) {
            const block = await this.blockResource.retrieve<NICmsMerchandiser.IData>(
                {
                    bareUrl: NICmsMerchandiser.configId,
                    variant: categoryId + '',
                    initContent: {
                        hidden: [],
                        order: []
                    }
                },
                undefined
            );

            if (block && block.id > 0) {
                this.loadedBlocks.set(categoryId, block);
                const onBlockDataChange = () => {
                    this.syncBlockIdsWithCategory(categoryId);
                    this.patchCategoryData(categoryId);
                };

                block.change.subscribe(onBlockDataChange);
                onBlockDataChange();
            }
        }

        // retrieve content data
        return this.loadedBlocks.get(categoryId);
    }

    getSourceProductIds(categoryId: number): number[] | undefined {
        return (this.categoryResource.getExisting(categoryId)?.data as ICategoryDataV3)._vmProducts;
    }

    notifyChange(categoryId: number) {
        this.categoryResource.getExisting(categoryId)?.change.next();
    }

    /**
     * Set up plugin on category resource to process category whenever it changes
     */
    private pluginCategoryResource() {
        this.categoryResource.addPlugin((category: Category<ICategoryData>) => {
            category.change.subscribe(() => this.onCategoryDataReset(category as Category<ICategoryDataV3>));
        });
        this.categoryResource.getAllExisting().forEach(category => this.onCategoryDataReset(category as Category<ICategoryDataV3>));
    }

    private onCategoryDataReset(category: Category<ICategoryDataV3>) {
        if (category.data?.products) {
            this.setOrgData(category);
            // makes sure config is loaded and patch category data
            this.provideConfig(category.id);
            this.patchCategoryData(category.id);
        }
    }

    /**
     * Replace category products array with the one from the block
     * Applied everytime category data is changed or block is loaded
     */
    private patchCategoryData(categoryId: number) {
        // only process if org products are already set
        const orgProducts = this.getSourceProductIds(categoryId);
        if (orgProducts) {
            // only if block is loaded already
            const block = this.loadedBlocks.get(categoryId);
            if (block) {
                // retrieve category
                const category = this.categoryResource.getExisting(categoryId) as Category<ICategoryDataV3>;
                if (category) {
                    const products = intersection(block.data?.content.order, orgProducts) || [];
                    if (category.data && !isEqual(category.data?.products, products)) {
                        category.data.products = products;
                        category.change.next();
                    }
                }
            }
        }
    }

    /**
     * Sets in map originally loaded products for category
     * Triggered when category data is loaded
     */
    private setOrgData(category: Category<ICategoryDataV3>) {
        if (category.data && !category.data._vmProducts) {
            category.data._vmProducts = category.data.products;
            this.syncBlockIdsWithCategory(category.id);
        }
    }

    /**
     * Only applied when block is loaded and category data is known
     * triggered everytime category data is changed or block is loaded
     */
    private syncBlockIdsWithCategory(categoryId: number) {
        const block = this.loadedBlocks.get(categoryId);
        if (block) {
            const orgProducts = this.getSourceProductIds(categoryId);
            if (orgProducts) {
                // patch both org data and scope data
                [block.rawData, ...block.getScopeDataEntries()].forEach(data => {
                    if (data) {
                        const content = data.content;
                        const orgVisibleProducts = difference(difference(orgProducts, content.hidden), content.order);
                        [content.hidden, content.order].forEach(arr => {
                            arr.forEach(i => {
                                if (!orgProducts.includes(i)) {
                                    arr.splice(arr.indexOf(i), 1);
                                }
                            });
                        });
                        content.order.push(...orgVisibleProducts);
                    }
                });
            }
        }
    }
}
